import { useMemo, useState, type ReactNode } from "react";
import { ArrowLeft, Check, ChevronRight } from "lucide-react";
import { CommandEmpty, CommandGroup, CommandItem, CommandList, CommandSeparator } from "@nexus/ui";

import type { RecentResource } from "@/contexts/AppContext";
import type { MultiChartEvent } from "@/hooks/live/useMultiChartEvents";
import { formatDateTime, type TimezoneMode } from "@/lib/dateFormat";

function searchableText(event: MultiChartEvent): string {
  return [
    event.event_label,
    event.collection_group,
    event.event_id,
    event.event_type_label,
    ...(event.tenants?.map((tenant) => tenant.label) ?? []),
    ...(event.protectedEvents?.map((item) => item.channel) ?? []),
  ]
    .join(" ")
    .toLocaleLowerCase();
}

function EventOption({
  event,
  selected,
  timezone,
  onSelect,
}: {
  event: MultiChartEvent;
  selected: boolean;
  timezone: TimezoneMode;
  onSelect: () => void;
}) {
  return (
    <CommandItem value={event.event_id} onSelect={onSelect} className="gap-3 py-2">
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-primary">{event.event_label}</span>
        <span className="mt-0.5 block truncate text-xs text-muted">
          {event.tenants?.map((tenant) => tenant.label).join(" · ") ||
            event.collection_group ||
            "Uncategorised"}{" "}
          · {formatDateTime(event.event_start, timezone)}
        </span>
      </span>
      {selected ? <Check className="size-4 shrink-0 text-brand-cyan" aria-hidden /> : null}
    </CommandItem>
  );
}

/**
 * The search/recents/collection-browsing logic shared by every event picker
 * surface (Event Protection's full-width bar, Event Monitor/Overview's
 * compact header pill): one `<Command>`-driven result list, built from the
 * same events and the same rules, regardless of how the trigger around it
 * looks. The caller owns the popover's open state and where the search input
 * and `resultsList` are placed.
 */
export function useEventSearch({
  events,
  recents,
  selectedEventId,
  timezone,
  onSelect,
  onClear,
}: {
  events: MultiChartEvent[];
  recents: RecentResource[];
  selectedEventId: string | null;
  timezone: TimezoneMode;
  onSelect: (event: MultiChartEvent) => void;
  /** When provided, a "Clear selection" entry appears above the default list. */
  onClear?: () => void;
}) {
  const [query, setQuery] = useState("");
  const [browseCollection, setBrowseCollection] = useState<string | null>(null);

  const recentlyViewed = useMemo(() => {
    const byId = new Map(events.map((event) => [event.event_id, event]));
    return recents
      .filter((recent) => recent.kind === "event")
      .map((recent) => byId.get(recent.id))
      .filter((event): event is MultiChartEvent => Boolean(event))
      .slice(0, 5);
  }, [events, recents]);

  const recentViewedIds = new Set(recentlyViewed.map((event) => event.event_id));
  const recentEvents = events.filter((event) => !recentViewedIds.has(event.event_id)).slice(0, 5);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const searchResults = normalizedQuery
    ? events.filter((event) => searchableText(event).includes(normalizedQuery))
    : [];
  const collections = useMemo(() => {
    const counts = new Map<string, number>();
    for (const event of events) {
      const collection = event.collection_group || "Uncategorised";
      counts.set(collection, (counts.get(collection) ?? 0) + 1);
    }
    return [...counts.entries()].sort(([left], [right]) => left.localeCompare(right));
  }, [events]);
  const matchingCollections = normalizedQuery
    ? collections.filter(([collection]) => collection.toLocaleLowerCase().includes(normalizedQuery))
    : [];
  const collectionIsExactMatch = matchingCollections.some(
    ([collection]) => collection.toLocaleLowerCase() === normalizedQuery,
  );
  const collectionEvents = browseCollection
    ? events.filter((event) => (event.collection_group || "Uncategorised") === browseCollection)
    : [];

  function onQueryChange(value: string) {
    setQuery(value);
    if (value) setBrowseCollection(null);
  }

  function reset() {
    setQuery("");
    setBrowseCollection(null);
  }

  function select(event: MultiChartEvent) {
    onSelect(event);
    reset();
  }

  const collectionSearchGroup =
    matchingCollections.length > 0 ? (
      <CommandGroup heading="Collections">
        {matchingCollections.map(([collection, count]) => (
          <CommandItem
            key={collection}
            value={`collection-${collection}`}
            onSelect={() => {
              setQuery("");
              setBrowseCollection(collection);
            }}
            className="gap-3"
          >
            <span className="min-w-0 flex-1 truncate">{collection}</span>
            <span className="text-xs tabular-nums text-muted">{count}</span>
            <ChevronRight className="size-4 text-muted" aria-hidden />
          </CommandItem>
        ))}
      </CommandGroup>
    ) : null;
  const eventSearchGroup =
    searchResults.length > 0 ? (
      <CommandGroup heading="Events">
        {searchResults.map((event) => (
          <EventOption
            key={event.event_id}
            event={event}
            selected={event.event_id === selectedEventId}
            timezone={timezone}
            onSelect={() => select(event)}
          />
        ))}
      </CommandGroup>
    ) : null;

  const clearItem: ReactNode =
    onClear && selectedEventId && !normalizedQuery && !browseCollection ? (
      <>
        <CommandGroup>
          <CommandItem
            value="clear-event-selection"
            onSelect={() => {
              onClear();
              reset();
            }}
            className="gap-3 text-secondary"
          >
            <span className="flex-1 font-medium">Clear selection</span>
          </CommandItem>
        </CommandGroup>
        <CommandSeparator />
      </>
    ) : null;

  const resultsList = (
    <CommandList>
      {clearItem}
      {normalizedQuery ? (
        searchResults.length > 0 || matchingCollections.length > 0 ? (
          <>
            {collectionIsExactMatch ? collectionSearchGroup : eventSearchGroup}
            {matchingCollections.length > 0 && searchResults.length > 0 ? (
              <CommandSeparator />
            ) : null}
            {collectionIsExactMatch ? eventSearchGroup : collectionSearchGroup}
          </>
        ) : (
          <CommandEmpty className="block">
            No events match “{query.trim()}”
            <button
              type="button"
              onClick={() => setQuery("")}
              className="mx-auto mt-2 block font-medium text-brand-cyan hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            >
              Clear search
            </button>
          </CommandEmpty>
        )
      ) : browseCollection === "" ? (
        <>
          <CommandItem onSelect={() => setBrowseCollection(null)} className="gap-2">
            <ArrowLeft className="size-4 text-muted" aria-hidden />
            <span className="font-medium">Back to events</span>
          </CommandItem>
          <CommandSeparator />
          <CommandGroup heading="Collections">
            {collections.map(([collection, count]) => (
              <CommandItem
                key={collection}
                value={collection}
                onSelect={() => setBrowseCollection(collection)}
                className="gap-3"
              >
                <span className="min-w-0 flex-1 truncate">{collection}</span>
                <span className="text-xs tabular-nums text-muted">{count}</span>
                <ChevronRight className="size-4 text-muted" aria-hidden />
              </CommandItem>
            ))}
          </CommandGroup>
        </>
      ) : browseCollection ? (
        <>
          <CommandItem onSelect={() => setBrowseCollection("")} className="gap-2">
            <ArrowLeft className="size-4 text-muted" aria-hidden />
            <span className="font-medium">Back to collections</span>
          </CommandItem>
          <CommandSeparator />
          <CommandGroup heading={browseCollection}>
            {collectionEvents.map((event) => (
              <EventOption
                key={event.event_id}
                event={event}
                selected={event.event_id === selectedEventId}
                timezone={timezone}
                onSelect={() => select(event)}
              />
            ))}
          </CommandGroup>
        </>
      ) : events.length === 0 ? (
        <CommandEmpty className="block">
          No recent events available in the last three days.
        </CommandEmpty>
      ) : (
        <>
          {recentlyViewed.length > 0 ? (
            <CommandGroup heading="Recently viewed">
              {recentlyViewed.map((event) => (
                <EventOption
                  key={event.event_id}
                  event={event}
                  selected={event.event_id === selectedEventId}
                  timezone={timezone}
                  onSelect={() => select(event)}
                />
              ))}
            </CommandGroup>
          ) : null}
          {recentEvents.length > 0 ? (
            <CommandGroup heading="Recent events">
              {recentEvents.map((event) => (
                <EventOption
                  key={event.event_id}
                  event={event}
                  selected={event.event_id === selectedEventId}
                  timezone={timezone}
                  onSelect={() => select(event)}
                />
              ))}
            </CommandGroup>
          ) : null}
          <CommandSeparator />
          <CommandGroup>
            <CommandItem onSelect={() => setBrowseCollection("")} className="gap-3">
              <span className="flex-1 font-medium">Browse by collection</span>
              <ChevronRight className="size-4 text-muted" aria-hidden />
            </CommandItem>
          </CommandGroup>
        </>
      )}
    </CommandList>
  );

  return { query, onQueryChange, resultsList, reset, browseCollection };
}
